import { NextResponse } from "next/server";
import { prisma } from "@/lib/db";
import { calculatePCorrectFromLogs } from "@/lib/scheduler/predictor";
import {
  DECAY_LAMBDA,
  FOCUS_SET_SIZE,
  MASTERY_THRESHOLD,
  NEW_ITEM_P_CORRECT,
  RETRY_DELAY_PROBLEMS,
} from "@/lib/scheduler/constants";

export async function POST(request: Request) {
  try {
    const body = await request.json();
    const { userId } = body;

    if (!userId) {
      return NextResponse.json({ error: "userId required" }, { status: 400 });
    }

    const now = new Date();

    // Get all problems with their states
    const problems = await prisma.problem.findMany({
      include: {
        group: true,
        userProblemStates: {
          where: { userId },
        },
        reviewLogs: {
          where: { userId },
          orderBy: { timestamp: "desc" },
          take: 10,
        },
      },
      orderBy: [
        { group: { sequence: "asc" } },
        { sequenceInGroup: "asc" },
      ],
    });

    // Calculate p_correct for each problem and build debug info
    const problemsDebug = problems.map((p) => {
      const state = p.userProblemStates[0];
      const logs = p.reviewLogs;

      const pCorrect = calculatePCorrectFromLogs(
        logs.map((l) => ({ timestamp: l.timestamp, wasCorrect: l.wasCorrect })),
        now
      );

      // Calculate hours since last attempt
      const lastLog = logs[0];
      const hoursSinceLast = lastLog
        ? (now.getTime() - lastLog.timestamp.getTime()) / (1000 * 60 * 60)
        : null;

      return {
        id: p.id,
        problemText: p.problemText,
        answer: p.answer,
        groupName: p.group.name,
        sequenceInGroup: p.sequenceInGroup,
        // State
        set: state?.set ?? "new",
        lastResult: state?.lastResult ?? null,
        lastAnsweredAt: state?.lastAnsweredAt ?? null,
        // Computed
        pCorrect: Math.round(pCorrect * 100) / 100,
        attemptCount: logs.length,
        correctCount: logs.filter((l) => l.wasCorrect).length,
        hoursSinceLast: hoursSinceLast ? Math.round(hoursSinceLast * 10) / 10 : null,
        // Recent history (last 5)
        recentHistory: logs.slice(0, 5).map((l) => ({
          wasCorrect: l.wasCorrect,
          userInput: l.userInput,
          timestamp: l.timestamp,
          presentedType: l.presentedType,
        })),
      };
    });

    // Get session stats
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const [todayLogs, totalLogs, recentSessions] = await Promise.all([
      prisma.reviewLog.findMany({
        where: {
          userId,
          timestamp: { gte: todayStart },
        },
        orderBy: { timestamp: "desc" },
      }),
      prisma.reviewLog.count({ where: { userId } }),
      prisma.studySession.findMany({
        where: { userId },
        orderBy: { startedAt: "desc" },
        take: 5,
      }),
    ]);

    // Count by set
    const setCounts = {
      new: problemsDebug.filter((p) => p.set === "new").length,
      focus: problemsDebug.filter((p) => p.set === "focus").length,
      old: problemsDebug.filter((p) => p.set === "old").length,
    };

    const focusProblems = problemsDebug.filter((p) => p.set === "focus");
    const focusCorrectCount = focusProblems.filter((p) => p.lastResult === "correct").length;
    const masteryPercent = focusProblems.length > 0
      ? Math.round((focusCorrectCount / focusProblems.length) * 100)
      : 0;

    // p_correct distribution for focus set
    const focusPCorrectBuckets = {
      hard: focusProblems.filter((p) => p.pCorrect < 0.5).length,
      medium: focusProblems.filter((p) => p.pCorrect >= 0.5 && p.pCorrect < 0.8).length,
      easy: focusProblems.filter((p) => p.pCorrect >= 0.8).length,
    };

    return NextResponse.json({
      // Constants
      constants: {
        DECAY_LAMBDA,
        FOCUS_SET_SIZE,
        MASTERY_THRESHOLD,
        NEW_ITEM_P_CORRECT,
        RETRY_DELAY_PROBLEMS,
      },
      // Set counts
      setCounts,
      focusCorrectCount,
      masteryPercent,
      focusPCorrectBuckets,
      // Today's activity
      todayStats: {
        total: todayLogs.length,
        correct: todayLogs.filter((l) => l.wasCorrect).length,
        accuracy: todayLogs.length > 0
          ? Math.round((todayLogs.filter((l) => l.wasCorrect).length / todayLogs.length) * 100)
          : 0,
      },
      // All-time stats
      allTimeStats: {
        totalAttempts: totalLogs,
      },
      // Recent sessions
      recentSessions: recentSessions.map((s) => ({
        id: s.id,
        mode: s.mode,
        startedAt: s.startedAt,
        endedAt: s.endedAt,
        completedCount: s.completedCount,
        correctCount: s.correctCount,
      })),
      // All problems with debug info
      problems: problemsDebug,
      // Focus set only (for quick view)
      focusSet: problemsDebug.filter((p) => p.set === "focus"),
      // Old set (recently graduated)
      oldSet: problemsDebug.filter((p) => p.set === "old"),
    });
  } catch (error) {
    console.error("Debug API error:", error);
    return NextResponse.json(
      { error: "Internal server error" },
      { status: 500 }
    );
  }
}
